import { GuardianReconciliationChargeDetailCreateRequest } from 'RtModels';
import { ReconciliationChargesSpreadsheetParser } from 'RtUi/app/rtVue/ReconciliationCharge/lib/bin/ReconciliationChargesSpreadsheetParser';
import { ReconciliationChargesCreateDataGrid } from 'RtUi/app/rtVue/ReconciliationCharge/lib/grids/ReconciliationChargesCreateDataGrid';
import { ReconciliationChargesResource } from 'RtUi/app/rtVue/ReconciliationCharge/lib/resources/ReconciliationChargesResource';
import { Confirmation } from 'RtUi/components/form/Confirmation';
import { FormWizard, FormWizardStep } from 'RtUi/components/form/FormWizard';
import { CollapsibleCard } from 'RtUi/components/ui/CollapsibleCard';
import { DragAndDropFileUploader } from 'RtUi/components/ui/DragAndDropFileUploader/DragAndDropFileUploader';
import { RtError } from 'RtUi/utils/errors/RtError';
import { FileUtils } from 'RtUi/utils/file/FileUtils';
import { Component } from 'react';
import { Button, Card, Col, Modal, Row, Table } from 'react-bootstrap';

interface IReconciliationChargesBulkUploadFormProps {
	onCancel?: () => void;
	onSave?: () => void;
	reconciliationId: number;
}

interface IReconciliationChargesBulkUploadFormState {
	chargeRecordsData: GuardianReconciliationChargeDetailCreateRequest[];
	processingChargesFile: boolean;
	wizardStep: number;
	error?: any;
	file?: File;
	isSubmitting?: boolean;
	isSamplesModalOpen?: boolean;
}

export class ReconciliationChargesBulkUploadForm extends Component<
	IReconciliationChargesBulkUploadFormProps,
	IReconciliationChargesBulkUploadFormState
> {
	public initialState: IReconciliationChargesBulkUploadFormState = {
		processingChargesFile: false,
		chargeRecordsData: [],
		wizardStep: 1,
		isSubmitting: false,
		error: undefined,
		file: undefined,
		isSamplesModalOpen: false
	};

	public state = this.initialState;

	public render() {
		return (
			<Row>
				<Col lg={10} xl={8}>
					<Card>
						<FormWizard
							error={this.state.error}
							isSubmitting={this.state.isSubmitting}
							currentStep={this.state.wizardStep}
							onChange={(wizardStep) => this.updateWizardStep(wizardStep)}
						>
							<FormWizardStep step={1} header={'Upload'}>
								<div className="text-end">
									<Button
										variant="link"
										onClick={() => this.toggleIsSamplesModalOpen(true)}
									>
										<span className="text-muted">
											<i className="fas fa-fw fa-info-circle" />
											&nbsp;
											<u>Example File</u>
										</span>
									</Button>
								</div>
								<DragAndDropFileUploader
									required
									accept={FileUtils.AcceptTypes.Spreadsheet}
									processing={this.state.processingChargesFile}
									value={this.state.file}
									onDrop={(file) => this.processFile(file)}
								/>
							</FormWizardStep>
							<FormWizardStep
								step={2}
								header={'Review & Submit'}
								disableBody
								onSubmit={() => this.createReconciliationCharges()}
							>
								<section className="table-info text-info">
									<section className="p-3 d-flex justify-content-start">
										<article>
											<i className="fas fa-fw fa-info-circle me-2" />
										</article>
										<article>
											Review the document below before submitting. Upload may
											take several minutes depending on the size of the
											submission.
										</article>
									</section>
								</section>
								<ReconciliationChargesCreateDataGrid
									data={this.state.chargeRecordsData}
								/>
							</FormWizardStep>
						</FormWizard>
						<div className="d-flex justify-content-center">
							<Button
								type="button"
								variant="white"
								className="mb-3 mt-1"
								disabled={this.state.isSubmitting}
								onClick={this.onCancel}
							>
								Cancel
							</Button>
						</div>
					</Card>
					<Modal
						show={this.state.isSamplesModalOpen}
						size="xl"
						onHide={() => this.toggleIsSamplesModalOpen()}
					>
						<Modal.Body className="bg-light rounded">
							<article className="d-flex justify-content-end mb-2">
								<Button
									variant="light"
									onClick={() => this.toggleIsSamplesModalOpen(false)}
								>
									<i className="fas fa-times fa-fw fa-lg" />
								</Button>
							</article>
							{this.renderSampleTable()}
						</Modal.Body>
					</Modal>
				</Col>
			</Row>
		);
	}

	private toggleIsSamplesModalOpen(
		isSamplesModalOpen = !this.state.isSamplesModalOpen
	) {
		this.setState({ isSamplesModalOpen });
	}

	private renderSampleTable() {
		const sampleData: string[][] = [
			['1234', 'customerAbc', "Jane's Charge", 'Yes', '5.50', '5.50', '5.50'],
			[
				'1235',
				'customerDef',
				"Anderson's Charge",
				'Yes',
				'5.50',
				'5.50',
				'5.50'
			],
			['1236', 'customerDgi', "Jose's Charge", 'Yes', '10.00', '5.00', '10,5']
		];

		return (
			<CollapsibleCard
				header="Example of Traffic Profile Spreadsheet"
				className="mb-3"
			>
				<Table size="sm" bordered className="bg-white" responsive>
					<thead>
						<tr>
							<th>invoice id</th>
							<th>item id</th>
							<th>label</th>
							<th>recurring</th>
							<th>account invoice charge</th>
							<th>expected charge</th>
							<th>invoice detail</th>
						</tr>
					</thead>
					<tbody>
						{sampleData.map((values, index) => (
							<tr key={index}>
								{values.map((value, innerIndex) => (
									<td className="text-monospace" key={innerIndex}>
										{value}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</Table>
			</CollapsibleCard>
		);
	}

	private updateWizardStep(wizardStep: number) {
		this.setState({ wizardStep, isSubmitting: false });
	}

	private async processFile(file: File) {
		const fileUtils = new FileUtils();

		try {
			this.setState({ processingChargesFile: true, error: undefined });

			const spreadsheet = await fileUtils.fileToSpreadsheet(file);

			const chargesParser = new ReconciliationChargesSpreadsheetParser(
				this.props.reconciliationId,
				spreadsheet
			);
			chargesParser.on('error', (eventMessage) => {
				this.setState(() => ({
					error: new RtError({ message: eventMessage })
				}));
			});
			const chargeRecordsData = chargesParser.parseRecords();
			this.setState({
				chargeRecordsData,
				file,
				processingChargesFile: false
			});
		} catch (error) {
			this.setState({ error, processingChargesFile: false });
		}
	}

	private async createReconciliationCharges() {
		if (this.state.error) {
			return;
		}

		try {
			const { onSave = () => null } = this.props;
			const reconciliationCharges = new ReconciliationChargesResource();
			this.setState({ isSubmitting: true, error: undefined });
			await reconciliationCharges.upload({
				charges: this.state.chargeRecordsData
			});
			this.setState(this.initialState, onSave);
		} catch (error) {
			this.setState({ error, isSubmitting: false });
		}
	}

	private onCancel = async () => {
		const { onCancel = () => null } = this.props;
		if (
			this.state.file ||
			this.state.processingChargesFile ||
			this.state.isSubmitting
		) {
			await this.showCancelUploadConfirmation();
		} else {
			this.setState(this.initialState, onCancel);
		}
	};

	private async showCancelUploadConfirmation() {
		const confirm = await Confirmation.create(
			<span>
				<span>
					You have not submitted your document. Are you sure you would like to
					cancel?
				</span>
			</span>,
			{
				confirmationBtnText: 'Yes, Cancel.',
				cancelTextBtnText: 'Go back'
			}
		);

		if (!confirm) {
			return;
		}

		this.setState(this.initialState, this.props.onCancel);
	}
}
